import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  Subscription, Category, LikedItem, HistoryEntry, RawFeedItem, FeedItem, SourceType,
} from './new-internet.model';
import { mergeFeedItems, FeedGroup } from './new-internet.util';

const FN_BASE = 'https://us-central1-steve-corry.cloudfunctions.net/feed';

export interface NewInternetData {
  subscriptions: Subscription[];
  categories: Category[];
  readHashes: Set<string>;
  likes: LikedItem[];
  history: HistoryEntry[];
}

export interface DiscoverResult {
  feedUrl: string;
  siteUrl: string;
  title: string;
  type: SourceType;
}

export interface StreamResult {
  items: FeedItem[];
  errors: { [feedUrl: string]: string };
}

@Injectable({ providedIn: 'root' })
export class NewInternetService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase, private http: HttpClient) {}

  private base(uid: string): string {
    return `newInternet/${uid}`;
  }

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // ---- combined Firebase state ----
  subscribeAll(uid: string): Observable<NewInternetData> {
    const subs$ = this.inCtx(() => this.db.list<Omit<Subscription, 'id'>>(`${this.base(uid)}/subscriptions`)
      .snapshotChanges().pipe(
        map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<Subscription, 'id'>) }))),
      ));
    const cats$ = this.inCtx(() => this.db.list<Omit<Category, 'id'>>(`${this.base(uid)}/categories`)
      .snapshotChanges().pipe(
        map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<Category, 'id'>) }))),
      ));
    const read$ = this.inCtx(() => this.db.object<{ [k: string]: number }>(`${this.base(uid)}/readState`)
      .valueChanges().pipe(
        map(v => new Set(Object.keys(v || {}))),
      ));
    const likes$ = this.inCtx(() => this.db.list<Omit<LikedItem, 'id'>>(`${this.base(uid)}/likes`)
      .snapshotChanges().pipe(
        map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<LikedItem, 'id'>) }))),
      ));
    const history$ = this.inCtx(() => this.db.list<Omit<HistoryEntry, 'id'>>(`${this.base(uid)}/history`)
      .snapshotChanges().pipe(
        map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<HistoryEntry, 'id'>) }))),
      ));
    return combineLatest([subs$, cats$, read$, likes$, history$]).pipe(
      map(([subscriptions, categories, readHashes, likes, history]) =>
        ({ subscriptions, categories, readHashes, likes, history })),
    );
  }

  // ---- subscriptions ----
  addSubscription(uid: string, sub: Omit<Subscription, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/subscriptions`).push(sub)).then(r => r.key!);
  }

  removeSubscription(uid: string, id: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/subscriptions/${id}`).remove());
  }

  // ---- categories ----
  addCategory(uid: string, name: string): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/categories`).push({ name })).then(r => r.key!);
  }

  // ---- read state ----
  markRead(uid: string, hash: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/readState/${hash}`).set(Date.now()));
  }

  // ---- likes ----
  addLike(uid: string, like: Omit<LikedItem, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/likes`).push(like)).then(r => r.key!);
  }

  removeLike(uid: string, id: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/likes/${id}`).remove());
  }

  setLikeCategory(uid: string, id: string, category: string | null): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/likes/${id}`).update({ category }));
  }

  // ---- history ----
  addHistory(uid: string, entry: Omit<HistoryEntry, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/history`).push(entry)).then(r => r.key!);
  }

  // ---- Cloud Function calls ----
  discover(url: string): Promise<DiscoverResult> {
    return this.http.get<DiscoverResult & { error?: string }>(
      FN_BASE, { params: { action: 'discover', url } },
    ).pipe(take(1)).toPromise().then(res => {
      if (!res || res.error) { throw new Error((res && res.error) || 'Discovery failed.'); }
      return res;
    });
  }

  fetchFeed(feedUrl: string): Promise<RawFeedItem[]> {
    return this.http.get<{ items?: RawFeedItem[]; error?: string }>(
      FN_BASE, { params: { action: 'fetch', url: feedUrl } },
    ).pipe(take(1)).toPromise().then(res => {
      if (!res || res.error || !res.items) {
        throw new Error((res && res.error) || 'Feed fetch failed.');
      }
      return res.items;
    });
  }

  // Fetch every subscription in parallel and merge. A feed that fails is
  // recorded in `errors` and simply contributes no items.
  async loadStream(
    subscriptions: Subscription[],
    readHashes: Set<string>,
    likedHashes: Set<string>,
  ): Promise<StreamResult> {
    const errors: { [feedUrl: string]: string } = {};
    const groups: FeedGroup[] = await Promise.all(
      subscriptions.map(async sub => {
        try {
          return { subscription: sub, items: await this.fetchFeed(sub.feedUrl) };
        } catch (e: any) {
          errors[sub.feedUrl] = (e && e.message) || 'Failed to load.';
          return { subscription: sub, items: [] as RawFeedItem[] };
        }
      }),
    );
    return { items: mergeFeedItems(groups, readHashes, likedHashes), errors };
  }
}
